import type { QueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { useState } from "react";
import { Sidebar } from "../components/sidebar";
import { LoadError } from "../components/page-state";
import { chaptersQuery, pagesQuery } from "../lib/queries";

export interface RouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
	component: Shell,
	notFoundComponent: NotFound,
	errorComponent: ({ error, reset }) => (
		<div className="p-8">
			<LoadError error={error} retry={reset} />
		</div>
	),
});

/**
 * Sidebar + content. The sidebar is the contents of the book; on narrow
 * screens it becomes a drawer behind a single button.
 */
function Shell() {
	const chapters = useQuery(chaptersQuery);
	const pages = useQuery(pagesQuery);
	const [open, setOpen] = useState(false);

	return (
		<div className="flex min-h-dvh">
			<aside
				className={`fixed inset-y-0 left-0 z-30 w-64 transform transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
				style={{ background: "var(--color-ink-900)", borderRight: "1px solid var(--color-ink-700)" }}
			>
				<Sidebar chapters={chapters.data ?? []} pages={pages.data ?? []} onNavigate={() => setOpen(false)} />
			</aside>
			{open && <button type="button" aria-label="Close menu" className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

			<div className="flex min-w-0 flex-1 flex-col">
				<header className="sticky top-0 z-10 flex h-12 items-center gap-3 px-4 lg:hidden" style={{ background: "var(--color-ink-900)", borderBottom: "1px solid var(--color-ink-700)" }}>
					<button type="button" className="btn" onClick={() => setOpen(true)} aria-label="Open menu">
						Menu
					</button>
					<Link to="/" className="font-display font-semibold">
						Chibi Codex
					</Link>
				</header>
				<main className="mx-auto w-full max-w-6xl flex-1 px-5 py-6 sm:px-8 sm:py-8">
					<Outlet />
				</main>
			</div>
		</div>
	);
}

function NotFound() {
	return (
		<div className="panel mx-auto my-10 max-w-md p-6 text-center">
			<h1 className="text-xl">That page is not in the codex</h1>
			<p className="mt-2 text-sm" style={{ color: "var(--color-ink-300)" }}>
				The link may be old, or the page was deleted.
			</p>
			<Link to="/" className="btn mt-4">
				Back to overview
			</Link>
		</div>
	);
}
